import { createContext, useContext, createElement } from 'react';
import type { ReactNode } from 'react';
import { API_BASE_URL } from './config';

export interface TenantConfig {
  slug: string;
  businessName: string;
  primaryColor: string;
  secondaryColor: string;
  logoUrl: string;
  quoteRecipientEmail: string;
  website: string;
  tagline: string;
  phone: string;
}

export const DEFAULT_TENANT: TenantConfig = {
  slug: 'default',
  businessName: 'CabinetVision',
  primaryColor: '#0066cc',
  secondaryColor: '#004499',
  logoUrl: '',
  quoteRecipientEmail: '',
  website: 'https://www.AgenticPNW.com',
  tagline: 'by AgenticPNW',
  phone: '503-319-5182',
};

// ─── Slug resolution ──────────────────────────────────────────────────────────

const SLUG_RE = /^[a-z0-9-]{1,64}$/;

export function resolveTenantSlug(): string {
  if (typeof window === 'undefined') return 'default';

  // 1. ?tenant= query param (primary mechanism, works on any domain)
  const params = new URLSearchParams(window.location.search);
  const fromParam = params.get('tenant');
  if (fromParam && SLUG_RE.test(fromParam)) return fromParam;

  // 2. Subdomain (future: once *.cabinetvision.com DNS is live)
  const parts = window.location.hostname.split('.');
  if (parts.length >= 3) {
    const sub = parts[0];
    if (sub !== 'www' && SLUG_RE.test(sub)) return sub;
  }

  return 'default';
}

// ─── Theme injection ──────────────────────────────────────────────────────────

function hexToRgb(hex: string): { r: number; g: number; b: number } | null {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex.trim());
  if (!result) return null;
  return {
    r: parseInt(result[1], 16),
    g: parseInt(result[2], 16),
    b: parseInt(result[3], 16),
  };
}

function darkenHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const d = (c: number) => Math.max(0, Math.round(c * 0.73));
  return `#${[d(rgb.r), d(rgb.g), d(rgb.b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function lightenHex(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return hex;
  const l = (c: number) => Math.min(255, Math.round(c + (255 - c) * 0.4));
  return `#${[l(rgb.r), l(rgb.g), l(rgb.b)].map((c) => c.toString(16).padStart(2, '0')).join('')}`;
}

function toSpaceSep(hex: string): string {
  const rgb = hexToRgb(hex);
  if (!rgb) return '0 102 204';
  return `${rgb.r} ${rgb.g} ${rgb.b}`;
}

export function applyTenantTheme(config: TenantConfig): void {
  const root = document.documentElement;
  const dark = config.secondaryColor || darkenHex(config.primaryColor);
  const light = lightenHex(config.primaryColor);

  // Space-separated RGB channels for Tailwind v3 opacity modifier support
  root.style.setProperty('--color-primary', toSpaceSep(config.primaryColor));
  root.style.setProperty('--color-primary-dark', toSpaceSep(dark));
  root.style.setProperty('--color-primary-light', toSpaceSep(light));

  if (config.businessName) {
    document.title = config.businessName;
  }
}

// ─── Loader ───────────────────────────────────────────────────────────────────

export async function loadTenantConfig(): Promise<TenantConfig> {
  const slug = resolveTenantSlug();

  if (slug === 'default') {
    applyTenantTheme(DEFAULT_TENANT);
    return DEFAULT_TENANT;
  }

  try {
    const url = `${API_BASE_URL}/api/v2/tenant/config?tenant=${encodeURIComponent(slug)}`;
    const res = await fetch(url, { signal: AbortSignal.timeout(3000) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const config = (await res.json()) as TenantConfig;
    applyTenantTheme(config);
    return config;
  } catch {
    // Silent fallback — never block the app from rendering
    applyTenantTheme(DEFAULT_TENANT);
    return DEFAULT_TENANT;
  }
}

// ─── React context ────────────────────────────────────────────────────────────

const TenantContext = createContext<TenantConfig>(DEFAULT_TENANT);

export function TenantProvider({
  config,
  children,
}: {
  config: TenantConfig;
  children: ReactNode;
}): JSX.Element {
  return createElement(TenantContext.Provider, { value: config }, children);
}

export function useTenant(): TenantConfig {
  return useContext(TenantContext);
}
